/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Component } from 'react';
import { css, StyleSheet } from 'aphrodite';

import { colors } from '@blend/consumer-components';
import { InputWrapperProps } from '../../TaskInputs/types';
import { AddressInput } from './types';
import AddressInputForm from './AddressInputForm';
import AddressInputFieldsContainer from './AddressInputFieldsContainer';
import AddressAutocompleteDropdown, {
  OnAddressSelectedOpts,
} from './AddressAutocompleteDropdown/AddressAutocompleteDropdown';
import AddressModal from './AddressModal';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { isEmpty } from '@utils/lodash';

/**
 *
 * NOTE! Please be aware that it's likely that you'll have to update MobileAddressInputWrapper along with this file.
 * They're fairly similar in behavior, but styling and functionality is different enough that it's better to keep
 * two separate wrapper components for desktop and mobile.
 *
 */

const styles = StyleSheet.create({
  desktopAddressInputWrapper: {
    width: '100%',
    marginBottom: 12,
  },
});

export interface DesktopAddressInputWrapperProps extends InputWrapperProps {
  display: string;
  existingAddresses: AddressInput.Address[];
  withCounty: boolean;
  locale?: AddressInput.Locale;
  isCityOnlyInput?: boolean;
}

interface DesktopAddressInputWrapperState {
  potentiallyInvalidAddress: AddressInput.Address | {};
  modalOpen: boolean;
}

class DesktopAddressInputWrapper extends Component<DesktopAddressInputWrapperProps, DesktopAddressInputWrapperState> {
  constructor(props: DesktopAddressInputWrapperProps) {
    super(props);
    this.state = {
      modalOpen: false,
      potentiallyInvalidAddress: props.input.value || {},
    };
  }

  onAddressChosenFromDropdown = (address: AddressInput.Address, opts: OnAddressSelectedOpts = { skipModal: false }) => {
    const { isCityOnlyInput } = this.props;
    // This is guaranteed to be a valid address, since we populated it from a currently existing known
    // valid address; in this scenario, we can skip the modal.
    if (opts.skipModal || isCityOnlyInput) {
      return this.onModalSubmitSuccess(address);
    }

    // Otherwise, we need to open the modal for validation and populate it with the chosen address.
    this.setState({
      potentiallyInvalidAddress: address,
      modalOpen: true,
    });
  };

  // TODO (RON-210): this is firing twice for some reason. figure out why.
  onModalSubmitSuccess = (address: AddressInput.Address) => {
    const { input } = this.props;
    if (address) {
      input.onChange(address);
      this.setState({ modalOpen: false, potentiallyInvalidAddress: address });
    }
  };

  forceCloseModal = () => {
    this.setState({
      modalOpen: false,
      potentiallyInvalidAddress: {},
    });
  };

  getClosestValidAddress = () => {
    const { input } = this.props;
    const localAddress = this.state.potentiallyInvalidAddress;
    const closestValidAddress = isEmpty(localAddress) ? input.value : localAddress;
    return closestValidAddress;
  };

  render() {
    const { input, meta, display, existingAddresses, withCounty, locale, disabled, isCityOnlyInput } = this.props;
    const headerText = `Confirm ${display}`;
    const error = meta.touched ? meta.error : undefined;
    return (
      <div className={css(styles.desktopAddressInputWrapper)}>
        <AddressModal
          headerText={headerText}
          contentLabel={headerText}
          isOpen={this.state.modalOpen}
          handleClose={this.forceCloseModal}
        >
          <AddressInputForm
            initialValues={this.state.potentiallyInvalidAddress}
            onSubmitSuccess={this.onModalSubmitSuccess}
          >
            <AddressInputFieldsContainer withCounty={withCounty} locale={locale} />
            <ActionButton data-dd-privacy="allow" color={colors.catalyst} type="submit">
              Save
            </ActionButton>
          </AddressInputForm>
        </AddressModal>
        <AddressAutocompleteDropdown
          showSearchIcon={false}
          onSelect={this.onAddressChosenFromDropdown}
          label={display}
          name={input.name}
          withCounty={withCounty}
          error={error}
          selectedValue={this.getClosestValidAddress()}
          existingAddresses={existingAddresses}
          disabled={disabled}
          isCityOnlyInput={isCityOnlyInput}
        />
      </div>
    );
  }
}

export default DesktopAddressInputWrapper;
